import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Payments.module.css';
import { Button } from '../../components/ui/Button';
import { buttonClassName } from '../../components/ui/buttonClassName';

const ALLOWED_PROOF_TYPES = new Set(['image/jpeg', 'image/png', 'application/pdf']);
const MAX_PROOF_BYTES = 10 * 1024 * 1024;

function validateProof(file: File): string | null {
  if (!ALLOWED_PROOF_TYPES.has(file.type)) return 'Attach a JPG, PNG or PDF file.';
  if (file.size > MAX_PROOF_BYTES) return 'Proof must be 10 MB or smaller.';
  return null;
}

export function ProofInput({
  value,
  onChange,
  disabled = false,
  required = true,
}: {
  value: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
  required?: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => {
    if (!value) {
      setPreview(null);
      return;
    }
    setPreview(null);
    if (value.type === 'application/pdf') {
      const url = URL.createObjectURL(value);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    const reader = new FileReader();
    reader.onload = () => setPreview(String(reader.result));
    reader.readAsDataURL(value);
    return () => {
      reader.abort();
    };
  }, [value]);
  return (
    <div>
      <div className={styles.proof}>
        <div>
          <strong>
            {value?.name ?? `No file selected (${required ? 'required' : 'optional'})`}
          </strong>
          <small>{t('payments.proofInput.hint')}</small>
        </div>
        <label className={buttonClassName('secondary', 'sm')} htmlFor={id}>
          {value ? 'Replace proof' : 'Attach proof'}
        </label>
        <input
          id={id}
          aria-label={t('payments.proofInput.attachmentLabel')}
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          disabled={disabled}
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0] ?? null;
            event.target.value = '';
            if (!file) return;
            const validation = validateProof(file);
            setError(validation);
            if (validation) return;
            onChange(file);
          }}
        />
      </div>
      {value && (
        <div className={styles.proofSelection}>
          {preview &&
            (value.type === 'application/pdf' ? (
              <a href={preview} target="_blank" rel="noopener noreferrer" title={t('payments.proofInput.opensInNewTab')}>
                {t('payments.proofInput.openPdf', { name: value.name })}
              </a>
            ) : (
              <img className={styles.proofThumbnail} src={preview} alt={t('payments.proofInput.previewAlt')} />
            ))}
          <Button
            type="button"
            variant="secondary" size="sm"
            disabled={disabled}
            onClick={() => {
              setError(null);
              setPreview(null);
              onChange(null);
            }}
          >
            {t('payments.proofInput.remove')}
          </Button>
        </div>
      )}
      {error && (
        <div role="alert" className={styles.error}>
          {error}
        </div>
      )}
    </div>
  );
}
